import { Box, Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { UsageEstimateDataRow } from './UsageEstimateDataRow';
import { UsageEstimateFooter } from './UsageEstimateFooter';
import { UsageEstimateHeader } from './UsageEstimateHeader';
import { UsageEstimateTitleRow } from './UsageEstimateTitleRow';
import { UsageEstimate } from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';

export interface IUsageEstimatePanelProps {
  usageEstimate: UsageEstimate;
}

export const UsageEstimatePanel = observer(({ usageEstimate }: IUsageEstimatePanelProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();

  /** State **/
  const [isUpdatingUsage, setIsUpdatingUsage] = useState(false);

  /** Computed **/
  const usageEstimateItems = usageEstimate.items && [...usageEstimate.items].sort((a, b) => (a.created_at > b.created_at ? 1 : -1));

  /** Methods **/

  /**
   * Update this panel's UsageEstimate
   * @param usageEstimate
   * @param usageEstimateIndex
   * @returns
   */
  const updateUsageEstimate = async (usageEstimate: UsageEstimate) => {
    if (isUpdatingUsage) return;
    else if (!estimateStore.currentEstimate) {
      console.warn('No estimate loaded!');
      return;
    }

    setIsUpdatingUsage(true);
    await estimateStore.updateUsageEstimate(usageEstimate.id, usageEstimate.title);
    setIsUpdatingUsage(false);
  };

  const addEstimateItem = async () => {
    // @ts-expect-error - this doesn't yet track which Usage/UsageType a new item belongs to,
    // so it can't supply the required `type`/`usageId` fields. Needs a UI decision before
    // this can be completed; left broken (as it already was) rather than guessed at.
    await estimateStore.createUsageEstimateItem({
      usageEstimateId: usageEstimate.id,
      title: 'New Usage Item',
    });
  };

  /** Render **/
  return (
    <Box margin="small" key={usageEstimate.id} fill="horizontal" round="15px" background="light-3" elevation="small">
      {/* Header */}
      <UsageEstimateHeader
        usageEstimate={usageEstimate}
        setUsageEstimate={(usageEstimate) => updateUsageEstimate(usageEstimate)}
      />

      {/* Data Table */}
      <Box border={{ side: 'bottom', size: 'xsmall' }}>
        <UsageEstimateTitleRow />
        {!!usageEstimateItems?.length &&
          usageEstimateItems?.map((item, i) => (
            <UsageEstimateDataRow key={item.id} usageEstimateItem={item} index={i} useColors />
          ))}
        {!usageEstimateItems?.length && (
          <Box align="center" pad="medium">
            <Text>No Usage Estimate Items found.</Text>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <UsageEstimateFooter addEstimateItem={addEstimateItem} />
    </Box>
  );
});
